import ROUTES from "./index.constant";
import { apiRequest } from "./apiHelper";
import { CampaignReviewData } from "PhishingManager/pages/Step5/Step5.types";
import { errorMessageHandler } from "PhishingManager/utils";

export interface CreateCampaignStepOnePayload {
  venza_tenant_id: number;
  packaged_template_id: number;
  language_id: number;
  name?: string;
  campaign_id?: number;
}
export interface CampaignPayload {
  venza_tenant_id: number;
  campaign_id: number;
}
export interface CreateCampaignStepTwoPayload {
  venza_tenant_id: number;
  campaign_id: number;
  emails: string[];
}

export const getPhishingCampaignsForTenant = async (
  tenantId: string | number | undefined,
  page: number,
  per_page: number,
  status?: string,
  campaginType?: string
) => {
  let url = `${ROUTES.CAMPAIGN_API}?venza_tenant_id=${tenantId}&page=${page}&limit=${per_page}`;
  if(status) url+= `&status=${status}`;
  if(campaginType) url+= `&campaign_type=${campaginType}`;
  return apiRequest(url);
};

export const discardPhishingCampaign = async (
  payload: CampaignPayload
) => {
  const { venza_tenant_id, campaign_id } = payload;
  const url = `${ROUTES.CAMPAIGN_API}/${campaign_id}?venza_tenant_id=${venza_tenant_id}`;
  return apiRequest(url, { method: "DELETE" });
};

export const releasePhishingCampaign = async (
  payload: CampaignPayload
) => {
  const { venza_tenant_id, campaign_id } = payload;
  const url = `${ROUTES.CAMPAIGN_API}/create/finalize?campaign_id=${campaign_id}&venza_tenant_id=${venza_tenant_id}`;
  return apiRequest(url, { method: "POST" });
}

export const getPhishingCampaignData = async (
  tenantId: string
) => {
  const url = `${ROUTES.TENANT_API}/${tenantId}/${ROUTES.TENANT_THRESHOLD_SUFFIX}`;
  return apiRequest(url);
};

export const getPhishingCampaignFilters = async (
  tenantId: string | number | undefined,
  value: string
) => {
  let query = `venza_tenant_id=${tenantId}`;
  if (!!value)  query = `${query}&filter=${value}`;
  const url = `${ROUTES.TEMPLATE_FILTER_API}?${query}`;
  return apiRequest(url);
};

export const getPhishingCampaignTemplates = async (
  tenantId: string | number | undefined,
  params: {
    name?: string;
    difficulties?: string[];
    languages?: number[];
    categories?: number[];
    page?: number;
    limit?: number;
  }
) => {
  if (!tenantId) return;

  const query = new URLSearchParams();

  if (params.name) query.append("name", params.name);

  params.difficulties?.forEach(d =>
    query.append("difficulties[]", d.toLowerCase())
  );

  params.languages?.forEach(l =>
    query.append("languages[]", String(l))
  );

  params.categories?.forEach(c =>
    query.append("categories[]", String(c))
  );

  if (params.page) query.append("page", String(params.page));
  if (params.limit) query.append("limit", String(params.limit));

  const url = `${ROUTES.TEMPLATE_API}?venza_tenant_id=${tenantId}&${query.toString()}`;
  return apiRequest(url);
};

export const getPhishingCampaignTemplatePreviewDetails = async (
  tenantId: string | number | undefined,
  templateId?: string | number | null,
  packagedTemplateLanguageId?: string | number | null,
) => {
  if (!tenantId || !templateId) return;

  const query = new URLSearchParams();
  query.append("venza_tenant_id", String(tenantId));

  if (packagedTemplateLanguageId) {
    query.append(
      "packaged_template_language_id",
      String(packagedTemplateLanguageId)
    );
  }
  
  const url = `${ROUTES.TEMPLATE_PREVIEW}/${templateId}?${query.toString()}`;
  return apiRequest(url);
};

export const createPhishingCampaignStepOne = async (
  payload: CreateCampaignStepOnePayload
) => {
  const { name } = payload;

  return apiRequest(ROUTES.CAMPAIGN_SUMBIT_STEP_1, { method: "POST", body: {
    ...payload,
    ...(name ? { name } : {})
  } });
};

export const createPhishingCampaignStepTwo = async (
  payload: CreateCampaignStepTwoPayload
) => {
  const { venza_tenant_id, campaign_id, emails } = payload;
  if (!emails || emails.length === 0) return;

  const query = new URLSearchParams();
  query.append("venza_tenant_id", String(venza_tenant_id));
  query.append("campaign_id", String(campaign_id));

  emails.forEach((email) => {
    query.append("emails[]", email);
  });

  const url = `${ROUTES.CAMPAIGN_SUMBIT_STEP_2}?${query.toString()}`;

  return apiRequest(url, { method: "POST" });
};

export const launchPhishingCampaign = async (
  payload: CampaignPayload
) => {
  const { venza_tenant_id, campaign_id } = payload;
  const query = new URLSearchParams();
  query.append("venza_tenant_id", String(venza_tenant_id));
  query.append("campaign_id", String(campaign_id));

  const url = `${ROUTES.LAUNCH_CAMPAIGN}?${query.toString()}`;
  return apiRequest(url, { method: "POST" });
};

export const getPhisingTimeZones = async (tenantId: string | number | undefined) => {
  const url = `${ROUTES.TIMEZONE_API}?venza_tenant_id=${tenantId}`;
  return apiRequest(url);
};

export const submitStep4 = async (
  payload: any,
  tenantId: string | number | undefined
) => {
  const url = `${ROUTES.CAMPAIGN_SUMBIT_STEP_4}?venza_tenant_id=${tenantId}`;
  return apiRequest(url, { method: "PATCH", body: payload });
};

export const reruncampaign = async (payload: CampaignPayload) => {
  return apiRequest(ROUTES.RE_RUN_CAMPAIGN, { method: "POST", body: payload });
};

export const submitStep3Org = async (payload: CampaignPayload) => {
  const { campaign_id, venza_tenant_id } = payload;
  const url = `${ROUTES.CAMPAIGN_SUMBIT_STEP_3}/org?venza_tenant_id=${venza_tenant_id}&campaign_id=${campaign_id}`;
  return apiRequest(url, { method: "POST" });
};

export const submitStep3Prop = async (payload: {
  venza_tenant_id: number;
  campaign_id: number;
  property_ids: number[];
}) => {
  const url = `${ROUTES.CAMPAIGN_SUMBIT_STEP_3}/properties`;
  return apiRequest(url, { method: "POST", body: payload });
};

export const submitStep3 = async (tab: "group" | "role" | "individual", payload: {
  venza_tenant_id: number;
  campaign_id: number;
  properties: Record<string, { property: number; groups: number[] }>[];
}) => {
  const { properties, venza_tenant_id, campaign_id } = payload;
  const url = `${ROUTES.CAMPAIGN_SUMBIT_STEP_3}/${tab}s?venza_tenant_id=${venza_tenant_id}&campaign_id=${campaign_id}`;
  return apiRequest(url, { method: "POST", body: { properties } });
};

export const getOrganizationUsers = async (venza_tenant_id: number) => {
  const url = `${ROUTES.TENANT_USERS_API}?venza_tenant_id=${venza_tenant_id}`;
  return apiRequest(url);
};

export const getAllProperties = async (venza_tenant_id: number, search: string, page: number, per_page: number) => {
  let query = `venza_tenant_id=${venza_tenant_id}&user_count=1`;
  if(search) query+= `&name=${search}`;
  const url = `${ROUTES.PROPERTIES_API}?${query}&page=${page}&limit=${per_page}`;
  return apiRequest(url);
};

export const getOptionsByProperties = async (tab: string, venza_tenant_id: number, properties: number[], selectAll: boolean) => {
  let query = `venza_tenant_id=${venza_tenant_id}&user_count=1`;
  if(!selectAll && properties.length) properties.forEach((propertyId) => query+= `&properties[]=${propertyId}`);
  if(selectAll) query+= `&select_all=1`;
  const url = tab === "group" ? ROUTES.GROUP_API : ROUTES.ROLES_API;
  return apiRequest(`${url}?${query}`);
}

export const getUsersByProperties = async (venza_tenant_id: number, properties: number[], search: string, page: number, limit: number, selectAll: boolean) => {
  let query = `venza_tenant_id=${venza_tenant_id}&user_count=1&page=${page}&limit=${limit}`;
  if(!selectAll && properties.length) properties.forEach((propertyId) => query+= `&groups[]=${propertyId}`);
  if(selectAll) query+= `&select_all=1`;
  if(search) query+=`&search=${search}`;
  const url = `${ROUTES.INDIVIDUAL_API}?${query}`;
  return apiRequest(url);
}

export const getOverAllReport = async (venza_tenant_id: number) => {
  const url = `${ROUTES.REPORT_OVERALL_API}?venza_tenant_id=${venza_tenant_id}`;
  return apiRequest(url);
};

export const getCoreMetricsData = async (venza_tenant_id: number, campaign_id: number) => {
  const url = `${ROUTES.CORE_METRICS_API}?venza_tenant_id=${venza_tenant_id}&campaign_id=${campaign_id}`;
  return apiRequest(url);
};

export const getPropertiesMetrics = async (venza_tenant_id: number, campaign_id: number) => {
  const url = `${ROUTES.PROPERTIES_METRICS_API}?venza_tenant_id=${venza_tenant_id}&campaign_id=${campaign_id}`;
  return apiRequest(url);
};

export const getIndividualUserData = async (venza_tenant_id: number, campaign_id: number) => {
  const url = `${ROUTES.INDIVIDUAL_METRICS_API}/${campaign_id}?venza_tenant_id=${venza_tenant_id}`;
  return apiRequest(url);
};

export const fetchCampaignReviewData = async (
  tenantId: number | string,
  campaignId: number | string
): Promise<{ data: CampaignReviewData; error: null } | { data: null; error: string }> => {
  try {
    if (!tenantId || !campaignId) return { data: null, error: "Missing tenantId or campaignId" };
    const res = await apiRequest(`${ROUTES.CAMPAIGN_API}/${campaignId}?venza_tenant_id=${tenantId}`);
    const data: CampaignReviewData = { id: Number(campaignId), ...(res?.data || {}) };
    return { data, error: null };
  } catch (error: any) {
    return { data: null, error: [422, 500].includes(error.statusCode) ? "We encountered an unexpected issue. Please try again or visit Help Center for more." : errorMessageHandler(error) };
  }
};